import { useMemo, useState } from 'react'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { SelectableTagModel } from '@app/types/TagModel'
import { useTranslations } from '@i18n/useLanguage'
import { TextLink } from '@app/components/core/link/TextLink'
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons'

export type TagSelectProps<TKey extends string | number> = {
  tags: SelectableTagModel<TKey>[]
  collapsedAmountVisible?: number
  onSelect: (id: TKey) => void
}

export const TagSelect = <TKey extends string | number>({
  tags,
  collapsedAmountVisible = 5,
  onSelect,
}: TagSelectProps<TKey>) => {
  const translations = useTranslations().app.common
  const [expanded, setExpanded] = useState(false)
  const hasMore = tags.length > collapsedAmountVisible
  const shownTags = useMemo(
    () => (expanded ? tags : tags.slice(0, collapsedAmountVisible)),
    [expanded, tags, collapsedAmountVisible],
  )

  return (
    <Row
      style={{
        flexWrap: 'wrap',
        gap: '0.5rem',
      }}
    >
      {shownTags.map((tag) => (
        <Tag
          key={tag.key}
          label={tag.value}
          variant={tag.selected ? 'subtle' : 'transparent'}
          size="small"
          onClick={() => onSelect(tag.key)}
        />
      ))}
      {hasMore && (
        <TextLink
          icon={expanded ? faMinus : faPlus}
          iconPosition="left"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? translations.less : translations.more}
        </TextLink>
      )}
    </Row>
  )
}
